<script lang="ts">
    import { activateIntegrations } from '$lib/global/events/loadAnalytics';
    import { trackCookies } from '$lib/global/tracking';
    import {
        setCookiePreferences,
        getCookiePreferences,
        getCookiePreferencesPresent,
    } from '$lib/helper';
    import { embedOptions } from '$lib/Stores/stores';
    import { cn } from '$lib/utils/cn';

    import isEmpty from 'lodash/isEmpty';
    import { onMount } from 'svelte';
    import { fly, fade } from 'svelte/transition';

    import { getActiveIntegrations } from './helper';

    import type { IntegrationData } from '$lib/components/ConsentOverlay/types';
    import type { Language } from '$lib/types/Language';
    import type { ComponentType } from 'svelte';

    export let layout = 'none';
    export let language: Language = 'en';
    export let campaignId = '';
    export let campaignVersionId = '';
    export let legalLink = '';
    export let termsLink = '';
    export let integrations: IntegrationData = {};
    export let forceVisible = false;
    export let disableCookiePoliciesLink = false;
    export let onAcceptClick: (() => void) | null = null;
    export let onCookiePoliciesLinkClick: (() => void) | undefined = undefined;

    // Get existing preferences
    let preferencesPresent = false;
    let mounted = false;
    let preferences = {};
    let accepted = false;

    let Banner: ComponentType;

    onMount(async () => {
        if (layout === 'normal') {
            Banner = (await import('./Normal/Normal.svelte')).default;
        } else if (layout === 'explicit') {
            Banner = (await import('./Explicit/Explicit.svelte')).default;
        } else if (layout === 'none') {
            activateIntegrations(integrations);
        }

        preferencesPresent = getCookiePreferencesPresent(campaignId, campaignVersionId);
        preferences = getCookiePreferences(campaignId, campaignVersionId);

        // only use the analytics if the user has not already accepted the cookies
        if (!isEmpty(preferences) && !forceVisible && !$embedOptions.preview) {
            acceptCookies(preferences);
        }

        mounted = true;
    });

    const acceptCookies = (selectedIntegrations: IntegrationData) => {
        accepted = true;
        setCookiePreferences(campaignId, campaignVersionId, selectedIntegrations);

        // Tracking
        const activeIntegrations = getActiveIntegrations(selectedIntegrations, integrations);
        const integrationNames = Object.keys(activeIntegrations).filter(
            (integration) => integration !== 'Perspective',
        );
        // tracking events are queued, so it can be called before activating integrations
        trackCookies(integrationNames);

        activateIntegrations(activeIntegrations);

        if (onAcceptClick) {
            onAcceptClick();
        }
    };

    const acceptPreviewCookies = () => {
        accepted = true;

        if (onAcceptClick) {
            onAcceptClick();
        }
    };

    $: showOverlay =
        (mounted &&
            (!preferencesPresent || $embedOptions.preview) &&
            !accepted &&
            layout !== 'none') ||
        forceVisible;

    const overlayClasses = cn('fun-flex fun-h-full fun-w-full fun-flex-col', {
        ['fun-items-center fun-justify-end fun-p-4 sm:fun-items-end']: layout === 'normal',
        ['fun-items-center fun-justify-end fun-p-2 sm:fun-justify-center']: layout === 'explicit',
    });
</script>

{#if showOverlay}
    <div
        id="ps-cookie-banner"
        class="fun-fixed fun-left-0 fun-top-0 fun-z-[999] fun-h-full fun-w-screen fun-bg-black/20 supports-[height:100dvh]:fun-h-dvh"
        transition:fade
    >
        <div class={overlayClasses} transition:fly={{ y: 50 }}>
            <svelte:component
                this={Banner}
                integrationsToShow={Object.keys(integrations)}
                storedIntegrations={preferences}
                acceptCookies={$embedOptions.preview ? acceptPreviewCookies : acceptCookies}
                {legalLink}
                {termsLink}
                {language}
                {disableCookiePoliciesLink}
                {onCookiePoliciesLinkClick}
            />
        </div>
    </div>
{/if}
